<script setup lang="ts">
import { ref } from "vue";

const { dataset, mlTask, estimators } = defineProps<{
  dataset: string;
  mlTask: string;
  estimators: string[];
}>();

const emit = defineEmits<{ selected: [dataset: string, estimator?: string] }>();

const isCollapsed = ref(false);
</script>

<template>
  <div class="group-selector-item" :class="{ collapsed: isCollapsed }">
    <header @click="emit('selected', dataset)">
      <div class="group-name text-base-regular">
        <div>
          <i class="icon icon-database" />
          <span>{{ dataset }}</span>
        </div>
        <div class="ml-task">{{ mlTask }}</div>
      </div>

      <div class="actions">
        <button
          class="toggle-children"
          @click.stop="isCollapsed = !isCollapsed"
          aria-label="Toggle collapsed"
        >
          <i class="icon icon-chevron-down" />
        </button>
      </div>
    </header>

    <div class="estimators">
      <div class="estimators-content">
        <div
          v-for="estimator in estimators"
          :key="estimator"
          class="estimator text-base-regular"
          @click.stop="emit('selected', dataset, estimator)"
        >
          <i class="icon icon-cube" />
          <span>{{ estimator }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.group-selector-item {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-8);

  header {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    padding: var(--numbers-4) var(--numbers-12);
    border-radius: var(--numbers-4);
    cursor: pointer;
    gap: var(--numbers-8);
    transition: background-color var(--animation-duration) var(--animation-easing);

    .group-name {
      overflow: hidden;
      min-width: 0;
      flex: 1;

      > div {
        display: flex;
        overflow: hidden;
        min-width: 0;
        align-items: center;
        gap: var(--numbers-8);

        span {
          overflow: hidden;
          min-width: 0;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .icon {
        color: var(--content-secondary);
      }

      .ml-task {
        color: var(--content-disabled);
      }
    }

    .actions {
      .toggle-children {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        margin: 0;
        margin-top: var(--numbers-4);
        background-color: transparent;
        color: var(--content-secondary);
        cursor: pointer;
        transform-origin: center;
        transition:
          transform var(--animation-duration) var(--animation-easing),
          color var(--animation-duration) var(--animation-easing);

        &:hover {
          color: var(--content-primary);
        }
      }
    }

    &:hover,
    &.selected {
      background-color: var(--background-tertiary);
    }
  }

  .estimators {
    display: grid;
    overflow: hidden;
    gap: var(--numbers-8);
    grid-template-rows: 1fr;
    transition: grid-template-rows var(--animation-duration) var(--animation-easing);

    .estimators-content {
      overflow: hidden;
      min-height: 0;
      padding-left: var(--numbers-24);
    }

    .estimator {
      padding: var(--numbers-4) 0;
      border-radius: var(--numbers-4);
      cursor: pointer;
      transition: background-color var(--animation-duration) var(--animation-easing);

      .icon {
        padding: 0 var(--numbers-12);
        border-left: solid var(--stroke-width-md) var(--border-tertiary);
        color: var(--content-secondary);
        vertical-align: middle;
      }

      &:hover,
      &.selected {
        background-color: var(--background-tertiary);
      }
    }
  }

  &.collapsed {
    header {
      .actions {
        .toggle-children {
          transform: rotate(180deg);
        }
      }
    }

    .estimators {
      grid-template-rows: 0fr;
    }
  }
}
</style>
